import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Pagination Component
 *
 * A reusable pagination component that displays page numbers with navigation controls.
 * Features:
 * - Previous/Next buttons with disabled states
 * - Page number buttons with current page highlighting
 * - Ellipsis for large page counts
 * - Responsive design with Tailwind CSS
 * - Icons from lucide-react
 *
 * @param {Object} props - Component props
 * @param {number} props.currentPage - The current active page (1-indexed)
 * @param {number} props.totalPages - Total number of pages
 * @param {Function} props.onPageChange - Callback function when page changes
 */
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  // Don't render pagination if there's only one page or less
  if (totalPages <= 1) {
    return null;
  }

  /**
   * Generate an array of page numbers to display
   * Shows a maximum of 7 page buttons with ellipsis for large page counts
   * Logic:
   * - Always show first and last page
   * - Show current page and 1 page on each side
   * - Show ellipsis when there's a gap
   */
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Total number of page buttons to show

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages with ellipsis
      const leftSiblingIndex = Math.max(currentPage - 1, 1);
      const rightSiblingIndex = Math.min(currentPage + 1, totalPages);

      const shouldShowLeftEllipsis = leftSiblingIndex > 2;
      const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;

      // Always show first page
      pages.push(1);

      // Show left ellipsis if needed
      if (shouldShowLeftEllipsis) {
        pages.push('ellipsis-left');
      }

      // Show pages around current page
      for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }

      // Show right ellipsis if needed
      if (shouldShowRightEllipsis) {
        pages.push('ellipsis-right');
      }

      // Always show last page
      if (totalPages !== 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page) => {
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  return (
    <div className="mt-8 flex justify-center">
      <div className="flex flex-col sm:flex-row items-center gap-4">
        {/* Page Info - Shows on mobile */}
        <div className="text-sm text-gray-600 sm:hidden">
          Page {currentPage} of {totalPages}
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center gap-2">
          {/* Previous Button */}
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            aria-label="Go to previous page"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Previous</span>
          </button>

          {/* Page Numbers */}
          <div className="flex items-center gap-1">
            {pageNumbers.map((page, index) => {
              // Render ellipsis
              if (typeof page === 'string' && page.startsWith('ellipsis')) {
                return (
                  <span
                    key={page}
                    className="px-2 py-2 text-sm text-gray-400"
                  >
                    ...
                  </span>
                );
              }

              // Render page button
              return (
                <button
                  key={page}
                  onClick={() => handlePageClick(page)}
                  className={`min-w-[2.5rem] px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    page === currentPage
                      ? 'text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:ring-blue-500'
                  }`}
                  aria-label={`Go to page ${page}`}
                  aria-current={page === currentPage ? 'page' : undefined}
                >
                  {page}
                </button>
              );
            })}
          </div>

          {/* Next Button */}
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            aria-label="Go to next page"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Page Info - Shows on desktop */}
        <div className="hidden sm:block text-sm text-gray-600">
          Page {currentPage} of {totalPages}
        </div>
      </div>
    </div>
  );
};

export default Pagination;
